import React, { useCallback, useEffect, useState } from "react";
import {
  GSForm,
  GSSidePanelPage,
  useFormValidation,
} from "golfstatus_react_components";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useMatch, useSearchParams } from "react-router-dom";

//import scss
import "./order-item-form.scss";
import {
  useNotificationAction,
  useNotificationBanner,
  useNotifications,
} from "../../../../hooks/notificationHooks";
import {
  GHIN_NUMBER_TYPE,
  HANDICAP_TYPE,
  getOrderForm,
  getPlayerForm,
  getSponsorForm,
  getTeamForm,
} from "../../../../forms/PackageForm";
import { useDispatch, useSelector } from "react-redux";
import {
  clearNotifications,
  getRegistrationOrderThunk,
  selectRegistrationCustomer,
  selectRegistrationOrder,
  updateInProgressPlayerThunk,
  updateInProgressSponsorThunk,
  updateInProgressTeamThunk,
  updateOrderForm,
  updateOrderThunk,
  updatePlayer,
  updateSponsor,
  updateSponsorOrderForm,
  updateTeam,
  updateTeamOrderForm,
} from "../../../../reducers/registrationOrderSlice";
import { getRouting } from "../../../../helpers/Converters";
import {
  ORDER_ID,
  ORDER_ITEM_ID,
  TOURNAMENT_ID,
  eventBaseRoute,
  getProp,
} from "../../../../routes/event";
import {
  clearUtility,
  clearUtilityNotifications,
  selectCroppedTarget,
  uploadFile,
} from "../../../../reducers/utilitySlice";
import { useTournament } from "../../../../hooks/tournamentHooks";
import { FULFILLED } from "../../../../app/api";
import { useGolfstatusNavigate } from "../../../../hooks/navigationHooks";

/**
 *
 * @typedef Properties
 * @type {object}
 * @property {string} formType type of form to be shown
 *
 * @param {Properties} props formType
 */

const FORM_TYPE = ":formType";

export const TEAM_FORM = "team";
export const PLAYER_FORM = "player";
export const SPONSOR_FORM = "sponsor";
export const ORDER_FORM = "order";

//Name the component
const OrderItemForm = (props) => {
  const { formType } = props;

  const notificationIDS = [
    "updateInProgressTeamThunk",
    "updateInProgressPlayerThunk",
    "uploadFile",
    "updateInProgressSponsorThunk",
    "updateOrderThunk",
  ];

  const cart = getRouting(eventBaseRoute, [
    TOURNAMENT_ID,
    "cart",
    ORDER_ID,
    ORDER_ITEM_ID,
    FORM_TYPE,
  ]);

  const checkout = getRouting(eventBaseRoute, [
    TOURNAMENT_ID,
    "cart",
    ORDER_ID,
    "checkout",
    ORDER_ITEM_ID,
    FORM_TYPE,
  ]);

  const cartMatch  = useMatch(cart);
  const checkoutMatch = useMatch(checkout);
  const match = cartMatch ?? checkoutMatch
  const orderItemID = getProp(match?.params, ORDER_ITEM_ID);
  const tournamentID = getProp(match?.params, TOURNAMENT_ID);
  const orderID = getProp(match?.params, ORDER_ID);

  const croppedImages = useSelector(selectCroppedTarget);

  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const itemID = searchParams?.get("id") ?? "";

  const registrationOrder = useSelector(selectRegistrationOrder);
  const currentItem = registrationOrder?.registrationOrderItems?.find?.(
    (item) => item?.id === orderItemID
  );

  const registrationCustomer = useSelector(selectRegistrationCustomer);

  const contactDetails = useSelector(selectRegistrationCustomer);

  const tournament = useTournament();

  const fallbackRoute = getRouting(eventBaseRoute, [
    tournamentID,
    "cart",
    orderID,
  ]);
  const navigate = useGolfstatusNavigate(fallbackRoute);

  const { utilityNotifications, orderNotifications } = useNotifications();

  const notifications = [
    ...utilityNotifications,
    ...orderNotifications,
  ].filter?.((notification) =>
    notificationIDS.some?.((wl) => wl === notification.id)
  );

  const goBack = useCallback(() => {
    dispatch(clearUtility());
    navigate(-1);
  }, [navigate, dispatch]);

  useEffect(() => {
    return () => dispatch(clearNotifications());
  }, [dispatch]);

  useNotificationAction(FULFILLED, orderNotifications, goBack);

  //order forms

  const findPlayer = (id) => {
    let player = currentItem.inProgressPlayers?.find?.((p) => p.id === id);
    if (player) {
      return player;
    }
    for (const t of currentItem.inProgressTournamentTeams) {
      let p = t.inProgressPlayers?.find?.((p) => p.id === id);
      if (p) {
        return p;
      }
    }
  };

  const findTeam = (id) => {
    return currentItem?.inProgressTournamentTeams?.find(
      (team) => team.id === id
    );
  };

  const findSponsor = (id) => {
    return currentItem?.inProgressSponsors?.find(
      (sponsor) => sponsor.id === id
    );
  };

  const initialValid = () => {
    if (formType === "sponsor") {
      if (itemID) {
        const sponsor = findSponsor(itemID);
        return !!sponsor?.name;
      }
    }
    return false;
  };

  const [orderContext, isValid, setIsValid] = useFormValidation(initialValid());
  //handicap type selection per in progress player, keyed by player id
  const [handicapTypes, setHandicapTypes] = useState({});

  orderContext.contactDetails = contactDetails;
  orderContext.tournament = tournament;
  orderContext.formType = formType;
  orderContext.orderItem = currentItem;

  orderContext.playerIsUsingContactDetails = (inProgressPlayer) => {
    return (
      inProgressPlayer.firstName === contactDetails.firstName &&
      inProgressPlayer.lastName === contactDetails.lastName &&
      inProgressPlayer.email === contactDetails.email &&
      inProgressPlayer.phone === contactDetails.phone
    );
  };

  orderContext.playerIsUsingContactDetails = (inProgressPlayer) => {
    return (
      inProgressPlayer.firstName === contactDetails.firstName &&
      inProgressPlayer.lastName === contactDetails.lastName &&
      inProgressPlayer.email === contactDetails.email &&
      inProgressPlayer.phone === contactDetails.phone
    );
  };

  orderContext.updateData = (value, property, id, type) => {
    const responseType = type ?? formType;

    if (responseType === ORDER_FORM) {
      setIsValid(true);
      let update = [...registrationOrder?.customFieldResponses];
      const index = update?.findIndex?.(cfr => cfr?.id === value.id)
      update[index] = value;
      dispatch(updateOrderForm(update));
    }

    if (responseType === TEAM_FORM) {
      const team = findTeam(id);
      if (team) {
        setIsValid(true);
        let update = [...team?.customFieldResponses];
        const index = update?.findIndex?.(cfr => cfr?.id === value.id)
        update[index] = value;
        dispatch(
          updateTeamOrderForm({
            responses: update,
            teamID: team?.id,
            orderItemID: orderItemID,
          })
        );
      }
    }

    if (responseType === SPONSOR_FORM) {
      const sponsor = findSponsor(id);
      if (sponsor) {
        setIsValid(true);
        let update = [...sponsor?.customFieldResponses];
        const index = update?.findIndex?.(cfr => cfr?.id === value.id)
        update[index] = value;
        dispatch(
          updateSponsorOrderForm({
            responses: update,
            sponsorID: sponsor?.id,
            orderItemID: orderItemID,
          })
        );
      }
    }

    if (responseType === PLAYER_FORM) {
      const player = findPlayer(id);
      if (player) {
        setIsValid(true);
        let playerUpdate = { ...player };
        let update = [...player?.customFieldResponses];
        const index = update?.findIndex?.(cfr => cfr?.id === value.id)
        update[index] = value;
        playerUpdate.customFieldResponses = update;
        playerUpdate.orderItemID = currentItem.id;
        dispatch(updatePlayer(playerUpdate));
      }
    }
  };

  orderContext.getData = () => {
    return registrationOrder?.customFieldResponses;
  };

  orderContext.getSponsorData = (id) => {
    if (id) {
      const sponsor = findSponsor(id);
      return sponsor;
    }
    return currentItem?.inProgressSponsors?.filter?.((i) => i.id === itemID);
  };

  orderContext.getTeams = () => {
    return currentItem?.inProgressTournamentTeams?.filter?.(
      (i) => i.id === itemID
    );
  };

  orderContext.getPlayers = () => {
    return currentItem?.inProgressPlayers?.filter?.((i) => i.id === itemID);
  };

  orderContext.updateTeamPlayerData = (value, property, id) => {
    const player = findPlayer(id);
    setIsValid(true);
    let update = { ...player, orderItemID: currentItem?.id };
    update[property] = value;
    dispatch(updatePlayer(update));
  };

  orderContext.getHandicapType = (inProgressPlayer) => {
    const selected = handicapTypes?.[inProgressPlayer?.id];
    if (selected) {
      return selected;
    }
    if ((inProgressPlayer?.handicap ?? "") !== "") {
      return HANDICAP_TYPE;
    }
    return GHIN_NUMBER_TYPE;
  };

  orderContext.setHandicapType = (inProgressPlayer, type) => {
    setHandicapTypes((current) => ({
      ...current,
      [inProgressPlayer?.id]: type,
    }));

    //only one of the two values can be submitted, clear the one being hidden
    const cleared =
      type?.value === HANDICAP_TYPE.value ? "ghinNumber" : "handicap";
    if ((inProgressPlayer?.[cleared] ?? "") !== "") {
      orderContext.updateTeamPlayerData("", cleared, inProgressPlayer?.id);
    }
  };

  orderContext.updateTeamData = (value, property, id) => {
    const team = findTeam(id);
    setIsValid(true);
    let update = { ...team, orderItemID: currentItem?.id };
    update[property] = value;
    dispatch(updateTeam(update));
  };

  orderContext.useContactInfo = (inProgressPlayer) => {
    const value = !orderContext.playerIsUsingContactDetails(inProgressPlayer);
    const player = findPlayer(inProgressPlayer.id);
    setIsValid(true);
    let update = { ...player, orderItemID: currentItem?.id };
    update.firstName = value ? contactDetails.firstName : "";
    update.lastName = value ? contactDetails.lastName : "";
    update.email = value ? contactDetails.email : "";
    update.phone = value ? contactDetails.phone : "";
    dispatch(updatePlayer(update));
  };

  orderContext.updateSponsorData = (value, property, id) => {
    const sponsor = findSponsor(id);
    setIsValid(true);
    let update = { ...sponsor, orderItemID: currentItem?.id };
    update[property] = value;
    dispatch(updateSponsor(update));
    return update;
  };

  orderContext.setBinding = (property, id) => {
    const player = findPlayer(id);

    return {
      value: player?.[property] ?? "",
      onChange: (e) => {
        const value = e?.target?.value;
        orderContext?.updateTeamPlayerData?.(value, property, id);
      },
      failedValidation: orderContext.validationFailed,
      required: checkRequired(property, player),
    };
  };

  orderContext.setSponsorBinding = (property, id) => {
    const sponsor = findSponsor(id);
    return {
      value: sponsor?.[property] ?? "",
      onChange: (e) => {
        orderContext?.updateSponsorData?.(e?.target?.value, property, id);
      },
      failedValidation: orderContext.validationFailed,
    };
  };

  const checkRequired = (property, player) => {
    switch (property) {
      case "firstName":
        return currentItem?.tournamentPackage?.requirePlayerFirstName;
      case "lastName":
        return currentItem?.tournamentPackage?.requirePlayerLastName;
      case "email":
        return currentItem?.tournamentPackage?.requirePlayerEmail;
      case "phone":
        return currentItem?.tournamentPackage?.requirePlayerPhone;
      case "ghinNumber":
        return (
          currentItem?.tournamentPackage?.requirePlayerGhinOrHandicap &&
          (player?.handicap ?? "") === ""
        );
      case "handicap":
        return (
          currentItem?.tournamentPackage?.requirePlayerGhinOrHandicap &&
          (player?.ghinNumber ?? "") === ""
        );
      default:
        return false;
    }
  };

  //

  const save = async () => {
    switch (formType) {
      case TEAM_FORM:
        await saveTeam();
        break;
      case PLAYER_FORM:
        await savePlayer();
        break;
      case SPONSOR_FORM:
        await saveSponsor();
        break;
      case ORDER_FORM:
        await saveOrder();
        break;
      default:
        break;
    }
    setIsValid(false);
  };

  const saveOrder = async () => {
    await dispatch(
      updateOrderThunk({ ...registrationOrder, registrationCustomer })
    );
  };

  const saveTeam = async () => {
    let team = { ...findTeam(itemID) };
    await dispatch(updateInProgressTeamThunk(team));
  };

  const savePlayer = async () => {
    const player = findPlayer(itemID);
    await dispatch(updateInProgressPlayerThunk(player));
  };

  const saveSponsor = async () => {
    let sponsor = findSponsor(itemID);
    if (croppedImages?.logo) {
      const logo = await dispatch(uploadFile(croppedImages.logo));
      if (logo) {
        sponsor = orderContext.updateSponsorData(
          `${process.env.REACT_APP_GOLFSTATUS_BASE_API_URL}${logo?.payload?.data?.location}`,
          "logo",
          itemID
        );
      }
    }
    if (croppedImages?.logoMobile) {
      const logoMobile = await dispatch(uploadFile(croppedImages.logoMobile));
      if (logoMobile) {
        sponsor = orderContext.updateSponsorData(
          `${process.env.REACT_APP_GOLFSTATUS_BASE_API_URL}${logoMobile?.payload?.data?.location}`,
          "logoMobile",
          itemID
        );
      }
    }
    await dispatch(updateInProgressSponsorThunk(sponsor));
  };

  const getDrawerActions = () => {
    let actions = [
      {
        name: "Save & Continue",
        isDisabled: !isValid,
        action: save,
        type: "black",
      },
      { name: "cancel", action: leftNavigation, type: "grey" },
    ];
    return actions;
  };

  const leftNavigation = () => {
    if (registrationOrder?.id) {
      dispatch(getRegistrationOrderThunk(registrationOrder?.id));
    }
    goBack();
  };

  const getNavigation = () => {
    //Add Title to your component
    let header = "Add Details";

    switch (formType) {
      case TEAM_FORM:
        header = "Team Details";
        break;
      case PLAYER_FORM:
        header = "Player Details";
        break;
      case SPONSOR_FORM:
        header = "Sponsor Details";
        break;
      default:
        header = "Additional Forms";
        break;
    }
    return {
      title: header,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getContent = () => {
    if (formType === TEAM_FORM) {
      return (
        <GSForm
          formTitle="Team Details"
          formSections={getTeamForm(orderContext)}
        ></GSForm>
      );
    }

    if (formType === PLAYER_FORM) {
      return (
        <GSForm
          formTitle="Player Details"
          formSections={getPlayerForm(orderContext)}
        ></GSForm>
      );
    }

    if (formType === SPONSOR_FORM) {
      return (
        <GSForm
          formTitle="Sponsor Details"
          formSections={getSponsorForm(orderContext)}
        ></GSForm>
      );
    }

    return (
      <GSForm
        formTitle="Additional Forms"
        formSections={getOrderForm(orderContext)}
      ></GSForm>
    );
  };

  const timeoutAction = () => {
    dispatch(clearUtilityNotifications());
    dispatch(clearNotifications());
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  const [banner] = useNotificationBanner({ notifications, timeoutAction });

  return (
    //name the component tag
    <order-item-form>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        content={getContent()}
        drawer={getDrawer()}
      ></GSSidePanelPage>
    </order-item-form>
  );
};

//export the new namet
export default OrderItemForm;
